import {
    TokenAmount,
    TransactionDetailRow,
    TransactionFeeSection,
    TransactionDialogContent,
    TransactionSection,
    TransactionArrow,
    Skeleton,
    AssetIcon,
    Tooltip,
} from "@/components";
import { useLockTransaction } from "@/hooks";
import { COMMON_TOKEN_IDS, TOKEN_IDS, type TokenId } from "@/types";
import { useCallback, useMemo } from "react";
import { useTokenApprovalV2 } from "@/hooks/transaction/useTokenApprovalV2";
import { getGatewayContractAddress } from "@/config";
import { mainnet } from "viem/chains";
import { truncateDecimals } from "@/utils";
import { ActionText } from "@/constants/text.enum";
import { InfinifiFees, applyFee } from "@/constants/fee.enum";
import { safaryTrackLockSuccess } from "@/services/analytics/safary.service";
import { useAccount } from "wagmi";
import { useTrackingConsent } from "../../hooks/shared/tracking-consent/useTrackingConsent";
import { Seperator } from "../shared/Seperator";
import { getTransactionLoadingState } from "./transaction-loading.utils";

export interface LockTransactionProps {
    lockAmount: number;
    duration: number;
    lockTokenId: TokenId;
    onClose?: () => void;
}

export function LockTransaction({ lockAmount, duration, lockTokenId, onClose }: LockTransactionProps) {
    const { address } = useAccount();
    const { canTrack } = useTrackingConsent();

    const {
        gasPriceUSD,
        projectedEarnings,
        apy,
        executeLockTransaction,
        isLoading: isLockTransactionLoading,
        siUSDExchange,
        liUSDExchange,
        isExecuting,
        isZap,
        zapAmountOut,
        zapTokenOut,
    } = useLockTransaction({
        lockAmount,
        duration,
        lockTokenId,
    });

    const approvalAmount = Number(truncateDecimals(lockAmount, 6));

    const { isApprovalLoading, withApproval, approvalLoadingText } = useTokenApprovalV2({
        tokenId: lockTokenId,
        amount: approvalAmount,
        spender: getGatewayContractAddress(mainnet.id),
    });

    const isMintAndLock = lockTokenId === TOKEN_IDS.USDC;
    const isUnstakeAndLock = lockTokenId === TOKEN_IDS.SIUSD;

    // siUSD -> iUSD -> liUSD (amount is siUSD)
    // Truncate siUSDExchange to 6 decimal places (no rounding up)
    const maxExchangePrecision = 18;

    const truncatedLockAmount = Number(truncateDecimals(lockAmount, maxExchangePrecision));
    const truncatedsiUSDExchange = Number(truncateDecimals(siUSDExchange, maxExchangePrecision));

    const altAmount = truncatedLockAmount * truncatedsiUSDExchange;

    // Must consider:
    // iUSD -> liUSD (amount is iUSD)
    // USDC -> iUSD -> liUSD (amount is iUSD)
    // and 0.3% fee to be applied
    const liUSDReceived = useMemo(() => {
        const truncatedInput = truncateDecimals(
            (isZap ? (zapAmountOut ?? 0) : isUnstakeAndLock ? altAmount : truncatedLockAmount) / liUSDExchange,
        );

        const numTruncDecimals = Number(truncatedInput);
        return applyFee(numTruncDecimals, InfinifiFees.Lock) as number;
    }, [isZap, zapAmountOut, isUnstakeAndLock, altAmount, truncatedLockAmount, liUSDExchange]);

    const LOCK_RECEIVE_TEXT = (
        <TokenAmount amount={isUnstakeAndLock ? altAmount.toFixed(6) : lockAmount} tokenId={TOKEN_IDS.IUSD} size="md" />
    );

    const LOCK_TEXT = <TokenAmount amount={lockAmount} tokenId={lockTokenId} size="md" />;

    const LOCK_IUSD_TEXT = (
        <TokenAmount
            amount={
                isZap ? (zapAmountOut ?? <Skeleton width={50} />) : isUnstakeAndLock ? altAmount.toFixed(6) : lockAmount
            }
            tokenId={TOKEN_IDS.IUSD}
            size="md"
        />
    );

    const UNSTAKE_IUSD_TEXT = <TokenAmount amount={lockAmount} tokenId={TOKEN_IDS.SIUSD} size="md" />;
    const LIUSD_TEXT = (
        <TokenAmount
            amount={liUSDReceived ? liUSDReceived.toFixed(6) : <Skeleton width={50} />} // NOTE: lock tx has 0.3% fee
            tokenId={TOKEN_IDS.LIUSD}
            suffix={`-${duration}w`}
            extra_desc={`, ${duration} week`}
            size="md"
        />
    );
    const DURATION_TEXT = `${duration} week${duration > 1 ? "s" : ""}`;
    const APY_TEXT = `${apy}% APY`;
    const PROJECTED_EARNINGS_TEXT = `${Number.parseFloat(projectedEarnings).toFixed(2)} iUSD`;
    const CONFIRM_TEXT = "Confirm Lock";

    const handleConfirm = useCallback(async () => {
        if (!address) return console.error("Wallet not connected");

        try {
            const hasExecuted = await withApproval(
                async () => {
                    await executeLockTransaction();
                    return true;
                },
                { isExecuting, amount: approvalAmount },
            );

            if (!hasExecuted) return;

            await Promise.resolve()
                .then(() => {
                    try {
                        canTrack &&
                            safaryTrackLockSuccess({
                                walletAddress: address,
                                duration,
                                tokenIn: lockTokenId,
                                tokenAmountIn: lockAmount,
                            });
                    } catch (e) {
                        console.error("Error tracking lock success:", e);
                    }
                })
                .finally(() => onClose?.());
        } catch (e) {
            console.error("Error executing lock transaction:", e);
        }
    }, [address, withApproval, executeLockTransaction, canTrack, lockAmount, lockTokenId, duration, onClose]);

    const { isLoading, loadingText } = getTransactionLoadingState({
        approvalLoadingText,
        isApprovalLoading,
        isExecuting,
        actionLoadingText: ActionText.LOCKING,
        isTransactionLoading: isLockTransactionLoading,
    });
    const isDisabled = isLoading || !lockAmount || !lockTokenId;

    return (
        <TransactionDialogContent
            onClose={onClose}
            onConfirm={handleConfirm}
            isLoading={isLoading}
            loadingText={loadingText}
            confirmText={CONFIRM_TEXT}
            disabled={isDisabled}
            className="w-[420px]"
        >
            <TransactionSection>
                {isMintAndLock && (
                    <div className="flex flex-col gap-2">
                        <TransactionDetailRow label="Mint" value={LOCK_TEXT} />
                        <TransactionArrow />
                        <TransactionDetailRow label="" value={LOCK_RECEIVE_TEXT} />
                        <Seperator />
                        <TransactionDetailRow label="Lock" value={LOCK_IUSD_TEXT} />
                    </div>
                )}
                {isUnstakeAndLock && (
                    <div className="flex flex-col gap-4">
                        <TransactionDetailRow label="Unstake" value={UNSTAKE_IUSD_TEXT} />
                        <TransactionArrow />
                        <TransactionDetailRow label="" value={LOCK_RECEIVE_TEXT} />
                        <Seperator />
                        <TransactionDetailRow label="Lock" value={LOCK_IUSD_TEXT} />
                    </div>
                )}
                {isZap && (
                    <div className="flex flex-col gap-4">
                        <TransactionDetailRow
                            label={
                                <Tooltip title={"PT tokens are zapped via. Pendle router"}>
                                    <span>Zap</span>
                                </Tooltip>
                            }
                            value={<TokenAmount amount={lockAmount} tokenId={lockTokenId} size="md" />}
                        />
                        <TransactionArrow />
                        {!zapAmountOut || !zapTokenOut ? (
                            <>
                                <TransactionDetailRow
                                    label=""
                                    value={
                                        <div className="w-full flex items-center justify-end gap-2">
                                            <Skeleton width={50} />
                                            <AssetIcon name={COMMON_TOKEN_IDS.USDC} size={16} />
                                            <span>{COMMON_TOKEN_IDS.USDC}</span>
                                        </div>
                                    }
                                />
                            </>
                        ) : (
                            <TransactionDetailRow
                                label=""
                                value={<TokenAmount amount={zapAmountOut} tokenId={zapTokenOut} size="md" />}
                            />
                        )}
                        <Seperator />

                        <TransactionDetailRow
                            label="Mint"
                            value={
                                <TokenAmount
                                    amount={zapAmountOut ?? <Skeleton width={50} />}
                                    tokenId={COMMON_TOKEN_IDS.USDC}
                                    size="md"
                                />
                            }
                        />
                        <TransactionArrow />
                        <TransactionDetailRow
                            label=""
                            value={
                                <TokenAmount
                                    amount={zapAmountOut ?? <Skeleton width={50} />}
                                    tokenId={COMMON_TOKEN_IDS.IUSD}
                                    size="md"
                                />
                            }
                        />

                        <Seperator />
                        <TransactionDetailRow label="Lock" value={LOCK_IUSD_TEXT} />
                    </div>
                )}
                <div className="flex flex-col gap-4 mt-2">
                    <TransactionDetailRow label="Duration" value={DURATION_TEXT} />
                    <TransactionDetailRow
                        label="Rate"
                        value={
                            <span className="inline-flex">
                                {APY_TEXT}
                                <sup className="mt-4">*</sup>
                            </span>
                        }
                        slotProps={{ value: { className: "text-secondary" } }}
                    />
                    <TransactionDetailRow label="Projected earnings" value={PROJECTED_EARNINGS_TEXT} />
                </div>
                <Seperator />
                <TransactionDetailRow label="Receive" value={LIUSD_TEXT} />
            </TransactionSection>
            <Seperator />
            <TransactionFeeSection gasPriceUSD={gasPriceUSD} />
        </TransactionDialogContent>
    );
}
